import React, { useEffect, useRef } from 'react';
import ImagePlaceholder from './ImagePlaceholder';

/* ── 2. Clip-path reveal hook ──
   Observes the parent container (defined size) so the observer fires
   reliably even before the img has loaded its natural height. */
function useClipReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const img = ref.current;
    if (!img) return;
    img.style.clipPath = 'inset(0 100% 0 0 round 12px)';
    const target = img.parentElement || img;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        img.style.transition = 'clip-path 0.9s cubic-bezier(0.77, 0, 0.175, 1)';
        img.style.clipPath = 'inset(0 0% 0 0 round 12px)';
        observer.disconnect();
      }
    }, { threshold: 0, rootMargin: '0px 0px -60px 0px' });
    observer.observe(target);
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function ScreenshotCard({ src, alt, imageName, stats, badge }) {
  const clipRef = useClipReveal();

  if (!src) {
    return (
      <div className="relative">
        <div
          className="absolute blur-3xl rounded-full"
          style={{
            width: '50%', height: '50%',
            background: 'rgba(63,196,227,0.12)',
            top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)',
            zIndex: 0,
          }}
        />
        <div
          className="relative z-10"
          style={{
            background: 'linear-gradient(135deg, rgba(63,196,227,0.4), rgba(63,196,227,0.06))',
            padding: '2px',
            borderRadius: '18px',
          }}
        >
          <ImagePlaceholder name={imageName || alt} height="340px" style={{ borderRadius: '16px', border: 'none' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="relative" style={{ paddingBottom: stats ? '36px' : '0' }}>
      {/* Dark gradient background */}
      <div style={{
        position: 'relative',
        background: 'linear-gradient(135deg, #0b5f77 0%, #062e3d 100%)',
        borderRadius: '24px',
        padding: '20px 20px 24px',
        overflow: 'visible',
      }}>
        {/* Decorative circles */}
        <div style={{
          position: 'absolute', top: '-40px', right: '-40px',
          width: '200px', height: '200px', borderRadius: '50%',
          background: 'rgba(63,196,227,0.12)', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-30px', left: '10%',
          width: '150px', height: '150px', borderRadius: '50%',
          background: 'rgba(63,196,227,0.08)', pointerEvents: 'none',
        }} />

        {/* Live badge — top right */}
        {badge && (
          <div style={{
            position: 'absolute',
            top: '-18px', right: '24px',
            background: '#ffffff',
            borderRadius: '12px',
            padding: '10px 16px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
            display: 'flex', alignItems: 'center', gap: '8px',
            zIndex: 20,
          }}>
            <span style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: '#22c55e', flexShrink: 0,
              boxShadow: '0 0 6px rgba(34,197,94,0.6)',
              display: 'inline-block',
            }} />
            <span style={{ fontSize: '12px', fontFamily: 'popinsMedium', color: '#1B1A28', whiteSpace: 'nowrap' }}>
              {badge}
            </span>
          </div>
        )}

        {/* ── 2. Clip-path reveal on screenshot ── */}
        <img
          ref={clipRef}
          src={src}
          alt={alt}
          loading="lazy"
          style={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
            display: 'block',
            position: 'relative', zIndex: 1,
          }}
        />
      </div>

      {/* Floating stat card — bottom left */}
      {stats && (
        <div style={{
          position: 'absolute',
          bottom: '-4px', left: '24px',
          background: '#ffffff',
          borderRadius: '16px',
          padding: '16px 22px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          minWidth: '180px',
          zIndex: 20,
        }}>
          <div style={{
            fontSize: '10px', color: '#9CA3AF', fontFamily: 'popinsMedium',
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px',
          }}>
            {stats.label}
          </div>
          <div style={{ fontSize: '30px', fontFamily: 'popinsBold', color: '#1B1A28', lineHeight: 1 }}>
            {stats.value}
          </div>
          {stats.sub && (
            <div style={{
              fontSize: '11px', color: '#3FC4E3', fontFamily: 'popinsMedium',
              marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px',
            }}>
              <span>↑</span> {stats.sub}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
